import { useState } from "react";
import { SkullIcon, StarIcon, XIcon } from "@phosphor-icons/react";
import { useRugLedger } from "../lib/useRugLedger";
import { useTrustedCreators } from "../lib/useTrustedCreators";

export const CreatorRugBadge = ({ creator, className = "" }) => {
  const { rugCountForCreator, isBlockedCreator } = useRugLedger();
  if (!isBlockedCreator(creator)) return null;
  const n = rugCountForCreator(creator);
  return (
    <span
      data-testid="creator-rug-badge"
      className={`inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 bg-danger/15 text-danger border border-danger/30 ${className}`}
      title={n > 0 ? `This creator has ${n} rug${n > 1 ? "s" : ""} on your record` : "You've blocked this dev"}
    >
      <SkullIcon size={11} weight="fill" /> {n > 0 ? `Dev rugged ${n}×` : "Blocked dev"}
    </span>
  );
};

export const RuggedButton = ({ mint, symbol, creator, size = 16, withLabel = false, className = "" }) => {
  const { isFlagged, flag, unflag } = useRugLedger();
  const flagged = isFlagged(mint);
  const [busy, setBusy] = useState(false);

  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      flagged ? await unflag(mint) : await flag(mint, symbol, creator);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid={`rug-btn-${mint}`}
      onClick={onClick}
      disabled={busy}
      title={flagged ? "Remove rug flag" : "Mark as rugged"}
      className={`inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
        flagged ? "text-danger" : "text-muted-foreground hover:text-danger"
      } ${withLabel ? "px-3 py-2 border " + (flagged ? "border-danger/40 bg-danger/10" : "border-white/15 hover:border-danger/40") : ""} ${className}`}
    >
      <SkullIcon size={size} weight={flagged ? "fill" : "regular"} />
      {withLabel && <span className="text-sm font-semibold">{flagged ? "Flagged rugged" : "Mark rugged"}</span>}
    </button>
  );
};

export const AddAlphaButton = ({ creator, size = 16, withLabel = false, className = "" }) => {
  const { isAlpha, addWallet, removeWallet, names } = useTrustedCreators();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const alpha = isAlpha(creator);

  if (!creator) return null;

  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    await addWallet(n, creator);
    setName("");
    setOpen(false);
  };

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (alpha) { removeWallet(creator); return; }
    setOpen(true);
  };

  return (
    <>
      <button
        data-testid={`alpha-btn-${creator}`}
        onClick={toggle}
        title={alpha ? "Remove from Alpha Devs" : "Add creator to an Alpha Dev"}
        className={`inline-flex items-center gap-1.5 transition-colors ${alpha ? "text-[#F0C000]" : "text-muted-foreground hover:text-[#F0C000]"} ${withLabel ? "px-3 py-2 border " + (alpha ? "border-[#C9A227]/40 bg-[#C9A227]/10" : "border-white/15 hover:border-[#C9A227]/40") : ""} ${className}`}
      >
        <StarIcon size={size} weight={alpha ? "fill" : "regular"} />
        {withLabel && <span className="text-sm font-semibold">{alpha ? "On Alpha list" : "Add to Alpha Devs"}</span>}
      </button>
      {open && (
        <div data-testid="add-alpha-modal" className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
          <div className="bg-card border border-[#C9A227]/40 max-w-sm w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <StarIcon size={20} weight="fill" className="text-[#F0C000]" />
              <h3 className="font-heading font-bold text-lg">Add to an Alpha Dev</h3>
            </div>
            <p className="text-xs text-muted-foreground">Name the dev this wallet belongs to. Reuse the same name to group all of a dev's wallets together.</p>
            <input
              list="alpha-dev-names"
              data-testid="add-alpha-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              autoFocus
              placeholder="Dev name (e.g. MrRunner)"
              className="w-full bg-black/40 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#C9A227]/60"
            />
            <datalist id="alpha-dev-names">
              {names.map((n) => <option key={n} value={n} />)}
            </datalist>
            <div className="flex gap-2">
              <button onClick={() => setOpen(false)} className="flex-1 px-4 py-2.5 border border-white/20 text-sm font-semibold hover:border-white/50 transition-colors">Cancel</button>
              <button data-testid="add-alpha-confirm" onClick={submit} className="flex-1 px-4 py-2.5 bg-[#F0C000] text-black text-sm font-semibold hover:bg-[#F0C000]/90 transition-colors">Add wallet</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


export const RugBuyConfirm = ({ open, count, symbol, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div data-testid="rug-buy-confirm" className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onCancel}>
      <div className="bg-card border border-danger/40 max-w-sm w-full p-6 space-y-4 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-12 h-12 mx-auto flex items-center justify-center bg-danger/10 border border-danger/40 text-danger">
          <SkullIcon size={26} weight="fill" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-lg">Repeat rugger warning</h3>
          <p className="text-sm text-muted-foreground mt-2">
            {count > 0
              ? <>You've flagged this developer for <span className="text-danger font-bold">{count} prior rug{count > 1 ? "s" : ""}</span>. Buying {symbol ? `$${symbol}` : "this token"} is high risk.</>
              : <>You've <span className="text-danger font-bold">blocked this developer</span>. Buying {symbol ? `$${symbol}` : "this token"} is high risk.</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="rug-confirm-cancel" onClick={onCancel} className="flex-1 px-4 py-2.5 border border-white/20 text-sm font-semibold hover:border-white/50 transition-colors">Cancel</button>
          <button data-testid="rug-confirm-proceed" onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-danger text-white text-sm font-semibold hover:bg-danger/90 transition-colors">Buy anyway</button>
        </div>
      </div>
    </div>
  );
};
